import { useState } from 'react';
import { ListPage, type Column } from '../../components/ListPage';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { api } from '../../lib/api';
import { dateLabel } from '../../lib/format';
import { Pill, Field, TextInput, TextArea, FormGrid } from '../../components/ui';

// 返工（质量检查不合格 → 厂内返工）
// POST /api/asset-return/rework · GET /api/asset-return/reworks?type=in_house

type ReworkRow = {
  id: string;
  number?: string;
  type?: string;
  reason?: string;
  notes?: string;
  status?: string;
  created_at?: string;
  original_inspection_number?: string;
  original_inspection_id?: string;
  customer_serial?: string;
  customer_name?: string;
  service_cycle_id?: string;
};

const STATUS: Record<string, { label: string; tone: 'ok' | 'info' | 'warn' }> = {
  draft: { label: '待返工', tone: 'warn' },
  in_progress: { label: '返工中', tone: 'info' },
  completed: { label: '已完成', tone: 'ok' },
};

export function ReworkPage() {
  const { user } = useAuth();
  const canWrite = can(user, 'returns', 'create');
  const [creating, setCreating] = useState(false);
  const [reloadSignal, setReloadSignal] = useState(0);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    original_inspection_id: '', service_cycle_id: '', reason: '', notes: '',
  });
  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setErr('');
    if (!f.original_inspection_id?.trim()) { setErr('请填写原不合格质检单 ID/单号'); return; }
    if (!f.reason?.trim()) { setErr('返工原因为必填'); return; }
    setBusy(true);
    try {
      await api.post('/api/asset-return/rework', {
        original_inspection_id: f.original_inspection_id.trim(),
        service_cycle_id: f.service_cycle_id.trim() || undefined,
        reason: f.reason.trim(),
        notes: f.notes.trim() || undefined,
        source_system: 'web-console',
        source_id: `RW/${f.original_inspection_id.trim()}/${Date.now()}`,
      });
      setCreating(false);
      setF({ original_inspection_id: '', service_cycle_id: '', reason: '', notes: '' });
      setReloadSignal((n) => n + 1);
    } catch (e: any) {
      setErr(e?.message || '创建失败');
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<ReworkRow>[] = [
    { header: '返工单号', render: (r) => <span className="num strong">{r.number || r.id}</span> },
    { header: '原质检单', render: (r) => <span className="muted">{r.original_inspection_number || r.original_inspection_id || '—'}</span> },
    { header: '序列号', render: (r) => <span className="muted">{r.customer_serial || '—'}</span> },
    { header: '客户', render: (r) => <span className="muted">{r.customer_name || '—'}</span> },
    { header: '原因', render: (r) => <span className="muted">{r.reason || '—'}</span> },
    { header: '日期', render: (r) => <span className="muted">{dateLabel(r.created_at)}</span> },
    {
      header: '状态',
      render: (r) => {
        const s = STATUS[r.status || 'draft'] || STATUS.draft;
        return <Pill tone={s.tone} label={s.label} />;
      },
    },
  ];

  return (
    <>
      {creating ? (
        <div className="panel" style={{ marginBottom: 12 }}>
          <div className="panel-head">
            <div>
              <h2>新建返工</h2>
              <div className="desc">质量检查发现不合格 → 厂内返工。原不合格记录保留不变；完成后须复检。</div>
            </div>
          </div>
          {err ? <div className="notice bad">{err}</div> : null}
          <FormGrid>
            <Field label="原不合格质检单 *" required>
              <TextInput value={f.original_inspection_id} onChange={(e) => set('original_inspection_id', e.target.value)} placeholder="QC-… 或质检单 ID" />
            </Field>
            <Field label="维修记录 ID（可选）">
              <TextInput value={f.service_cycle_id} onChange={(e) => set('service_cycle_id', e.target.value)} placeholder="留空则按质检单关联" />
            </Field>
            <Field label="返工原因 *" required span>
              <TextArea value={f.reason} onChange={(e) => set('reason', e.target.value)} placeholder="不合格现象、返工范围…" />
            </Field>
            <Field label="备注" span>
              <TextArea value={f.notes} onChange={(e) => set('notes', e.target.value)} />
            </Field>
          </FormGrid>
          <div className="detail-actions">
            <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '创建返工'}</button>
            <button className="btn" onClick={() => setCreating(false)}>取消</button>
          </div>
        </div>
      ) : null}
      <ListPage<ReworkRow>
        key={reloadSignal}
        title="返工"
        desc="质量检查发现不合格 → 厂内返工。原不合格不可覆盖；完成后须复检。"
        endpoint="/api/asset-return/reworks?type=in_house"
        columns={columns}
        rowId={(r) => String(r.id)}
        renderDetail={(r) => (
          <div className="k" style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>
            原质检单 {r.original_inspection_number || r.original_inspection_id || '—'} · {r.reason || '—'}
            {r.notes ? ` · ${r.notes}` : ''}
          </div>
        )}
        showYear={false}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setCreating((v) => !v)}>{creating ? '收起新建' : '新建返工'}</button>
        ) : undefined}
      />
    </>
  );
}
